import { useCallback, useEffect, useRef } from "react";

// Plays the spoken captions the server sends (neural-voice audio, one clip per translated chunk).
//
// Live speech creates three problems a plain "play each clip when it arrives" loop gets wrong:
//  1. Order — a clip can arrive after the next one (translation times vary), and speaking
//     sentence 4 before sentence 3 is confusing. Clips play in sequence order, waiting a moment
//     for a missing one before skipping it.
//  2. Falling behind — the translated voice is often slower than the speaker (Arabic especially),
//     so clips pile up. Playback speeds up a little (pitch preserved) as the backlog grows, and if
//     it grows past a limit the OLDEST clips are dropped: better to skip a sentence than to lag a minute.
//  3. Failed synthesis — the server marks such a chunk with `audio: null`; that one caption is read
//     with the phone's own built-in voice instead of leaving a silent gap.

export interface AudioChunk {
  sequenceNumber: number;
  /** null = the server could not synthesize this caption. */
  audio: ArrayBuffer | null;
  mimeType?: string;
  /** Present when audio is null: the caption to read with the built-in voice. */
  text?: string;
}

interface QueuedClip {
  seq: number;
  url: string | null;
  text?: string;
  arrivedAt: number;
}

const REORDER_WAIT_MS = 1200;
const MAX_BACKLOG = 4;
const SPEED_STEP = 0.12;
const MAX_RATE = 1.35;

// Tiny silent clip used to "unlock" audio playback from inside the user's tap (mobile browsers
// only allow sound after a gesture).
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";

export function useAudioPlayer(options: { enabled: boolean; onFallback: (text: string) => void }) {
  const { enabled } = options;
  const onFallbackRef = useRef(options.onFallback);
  onFallbackRef.current = options.onFallback;

  const elementRef = useRef<HTMLAudioElement | null>(null);
  const queueRef = useRef<QueuedClip[]>([]);
  const playingRef = useRef(false);
  const lastSeqRef = useRef(0);
  const enabledRef = useRef(enabled);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const element = useCallback(() => {
    if (!elementRef.current) {
      const el = new Audio();
      el.preload = "auto";
      elementRef.current = el;
    }
    return elementRef.current;
  }, []);

  const revoke = (clip: QueuedClip) => {
    if (clip.url) URL.revokeObjectURL(clip.url);
  };

  const pump = useCallback(() => {
    if (playingRef.current || !enabledRef.current) return;
    const queue = queueRef.current;
    queue.sort((a, b) => a.seq - b.seq);

    // Too far behind: drop the oldest clips.
    while (queue.length > MAX_BACKLOG) revoke(queue.shift()!);
    const next = queue[0];
    if (!next) return;

    // If the next clip isn't the one we expect, give the missing one a moment to arrive.
    const waited = Date.now() - next.arrivedAt;
    if (next.seq > lastSeqRef.current + 1 && lastSeqRef.current > 0 && waited < REORDER_WAIT_MS) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(pump, REORDER_WAIT_MS - waited);
      return;
    }

    queue.shift();
    lastSeqRef.current = Math.max(lastSeqRef.current, next.seq);

    if (!next.url) {
      if (next.text) onFallbackRef.current(next.text);
      pump();
      return;
    }

    const el = element();
    playingRef.current = true;
    const finish = () => {
      el.onended = null;
      el.onerror = null;
      URL.revokeObjectURL(next.url!);
      playingRef.current = false;
      pump();
    };
    el.onended = finish;
    el.onerror = finish;
    el.src = next.url;
    // Catch up gently when clips are queued behind this one; pitch stays natural.
    el.playbackRate = Math.min(MAX_RATE, 1 + SPEED_STEP * queue.length);
    (el as HTMLAudioElement & { preservesPitch?: boolean }).preservesPitch = true;
    el.play().catch(finish);
  }, [element]);

  const enqueue = useCallback(
    (chunk: AudioChunk) => {
      if (!enabledRef.current) return;
      const url = chunk.audio
        ? URL.createObjectURL(new Blob([chunk.audio], { type: chunk.mimeType || "audio/mpeg" }))
        : null;
      queueRef.current.push({ seq: chunk.sequenceNumber, url, text: chunk.text, arrivedAt: Date.now() });
      pump();
    },
    [pump],
  );

  /** Call from inside a tap/click handler when turning sound on. */
  const unlock = useCallback(() => {
    const el = element();
    el.src = SILENT_WAV;
    void el.play().catch(() => undefined);
  }, [element]);

  // Turning sound off (or leaving the page) stops everything and frees the audio.
  useEffect(() => {
    enabledRef.current = enabled;
    if (!enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      queueRef.current.forEach(revoke);
      queueRef.current = [];
      playingRef.current = false;
      lastSeqRef.current = 0;
      const el = elementRef.current;
      if (el) {
        el.onended = null;
        el.onerror = null;
        el.pause();
      }
    }
  }, [enabled]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      queueRef.current.forEach(revoke);
      elementRef.current?.pause();
    },
    [],
  );

  return { enqueue, unlock };
}
